import defaultStrings from './defaultStrings';

type TCallback = (...args: unknown[]) => unknown;

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ILocaleRecord extends Record<string, unknown> {}

export interface IBundles {
  [key: string]: TCallback;
}

export interface IBundle {
  strings?: ILocaleRecord;
  iso?: string;
  dates?: string;
  numbers?: string;
}

function buildValue(template: string | unknown, params: string[] = []) {
  if (typeof template !== 'string') return undefined;
  // If there are no dynamic value placeholders, return early
  if (template.indexOf('{') === -1) {
    return template;
  }

  let result = template;

  // Replace dynamic value placeholders with real values
  params.forEach((value, index) => {
    result = result.replace(`{${index}}`, value);
  });

  return result;
}

function getValue(key: string, pairs: ILocaleRecord) {
  const keyParts = key.split('.');
  let result = pairs[key] as string;

  // Basic support for a flat structure
  if (result) {
    return result;
  }

  result = keyParts.reduce((deepResult: unknown, part) => {
    if (!deepResult) {
      return undefined;
    }

    return (<ILocaleRecord>deepResult)[part];
  }, pairs) as string;

  return result;
}

export default {
  pairs: { ...defaultStrings } as ILocaleRecord,
  bundles: {} as IBundles,
  hasBundleBeenLoaded: false,
  bundleCallbacks: [] as TCallback[],

  set(key: string | ILocaleRecord, str?: string) {
    if (!!key && typeof key === 'object') {
      this.pairs = { ...this.pairs, ...key };
      return;
    }

    this.pairs[key] = str;
  },

  get(key: string | string[], ...params: string[]) {
    const lookUpKey = Array.isArray(key) ? key.join('') : key;
    const result = getValue(lookUpKey, this.pairs);
    const template = result || `??${lookUpKey}??`;

    return buildValue(template, params) || `??${lookUpKey}??`;
  },

  getDefault(key: string, defaultValue: string, ...params: string[]) {
    if (!defaultValue) {
      throw new Error('No default value provided. Did you mean to use locale.get?');
    }

    const result = getValue(key, this.pairs);
    const template = result || defaultValue;

    return buildValue(template, params) || defaultValue;
  },

  clear(resetToDefaultStrings?: boolean) {
    this.pairs = resetToDefaultStrings ? { ...defaultStrings } : {};
  },

  /**
   * Setup locale to support a range of different languages keyed by isoCode.
   * Each key must point to a function that accepts a callback that takes the actual locale
   * bundle for the given isoCode as its parameter. This design allows the freedom to load the
   * bundle for each iso code as you see fit, lazily or not.  It would be possible to make an
   * http call to a server as well to get the data.  When the call returns with your bundle you
   * just call the callback with it. The callback can be called synchronously or asynchronously
   *
   * For example (if using webpack and dynamic imports to lazy load your bundles):
   * {
   *    'en_US': callback => {
   *      import ( /* webpackChunkName: 'paymode_en_US' * / 'common/locale/paymode_en_US')
   *        .then((bundle => callback(bundle.default)));
   *    },
   *     'fr_CA': callback => {
   *      import ( /* webpackChunkName: 'paymode_fr_CA' * / 'common/locale/paymode_fr_CA')
   *        .then((bundle => callback(bundle.default)));
   *     }
   * }
   *
   * @param bundleHash Required isoCode keyed hash of bundle loading via callback functions.
   */
  initBundles: function initBundles(bundleHash: IBundles) {
    this.bundles = bundleHash;
  },

  /**
   * Fetches the bundle matching the passed in isoCode as setup in initBundles()
   *
   * @param isoCode Required, Key to use the the bundle, generally the language and locale codes.
   * @param callback Optional function to call with the bundle once it is loaded after it is used
   *     by locale itself.
   */
  fetch(isoCode: string, callback: TCallback) {
    const getBundle = this.bundles[isoCode];

    if (getBundle) {
      getBundle((bundle: IBundle) => {
        this.loadFetchedBundle(bundle, callback);
      });
    }
  },

  loadFetchedBundle(bundle: IBundle, callback: TCallback) {
    // Append localised strings
    if (bundle.strings) {
      this.set(bundle.strings);
    }

    if (typeof callback === 'function') {
      callback(bundle);
    }

    this.hasBundleBeenLoaded = true;
    this.runBundleLoadedCallbacks();
  },

  onBundleLoaded: function onBundleLoaded(callback: TCallback) {
    if (!this.bundleCallbacks) {
      this.bundleCallbacks = [];
    }

    this.bundleCallbacks.push(callback);

    if (this.hasBundleBeenLoaded) {
      callback();
    }
  },

  runBundleLoadedCallbacks: function runBundleLoadedCallbacks() {
    if (this.bundleCallbacks) {
      this.bundleCallbacks.forEach((callback) => {
        callback();
      });
    }
  },
};
